import React from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import { UserMinus } from 'lucide-react';

const removeButtonSx = (theme) => ({
  m: 0,
  minWidth: 42,
  color: theme.palette.secondary.main,
  borderColor: theme.palette.secondary.main,
  bgcolor: alpha(theme.palette.background.default, 0.82),
  boxShadow: `0 4px 0 ${darken(theme.palette.secondary.main, 0.58)}`,
  '&:hover': {
    color: theme.palette.secondary.main,
    borderColor: theme.palette.secondary.main,
    bgcolor: alpha(theme.palette.secondary.main, 0.12),
  },
  '&:active': { transform: 'translateY(3px)', boxShadow: 'none' },
});

export default function EventParticipantCard({ participant, saving = false, onRemove }) {
  const user = participant.user || {};
  const imageUrl = user.profile_image_url || '/dope_logo_192.png';
  const defaultImage = !user.profile_image_url;
  const subtitle = participant.role === 'creator' ? 'Organizer' : (
    participant.status === 'invited' ? 'Invited' : 'Attending'
  );

  return (
    <Box
      data-testid="event-participant-card"
      sx={(theme) => ({
        position: 'relative',
        width: '100%',
        height: { xs: 132, sm: 160 },
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        border: `3px solid ${theme.palette.green.main}`,
        borderRadius: 4,
        background:
          `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.96)} 0%, ` +
          `${theme.palette.background.default} 72%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
        boxShadow:
          `0 7px 0 ${alpha(theme.palette.green.main, 0.25)}, ` +
          `0 0 22px ${alpha(theme.palette.secondary.main, 0.22)}`,
      })}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: '0 auto 0 0',
          width: { xs: '58%', sm: 250 },
          overflow: 'hidden',
          WebkitMaskImage: 'linear-gradient(to right, #000 58%, transparent 100%)',
          maskImage: 'linear-gradient(to right, #000 58%, transparent 100%)',
        }}
      >
        <Box
          component="img"
          src={imageUrl}
          alt={`${user.username || 'Participant'}'s profile`}
          sx={{
            width: '100%',
            height: '100%',
            display: 'block',
            objectFit: defaultImage ? 'contain' : 'cover',
            objectPosition: 'left center',
          }}
        />
      </Box>

      <Stack
        spacing={1}
        alignItems="flex-start"
        sx={{
          position: 'relative',
          zIndex: 1,
          ml: { xs: '34%', sm: '180px' },
          pr: participant.permissions?.can_remove ? 7 : 2,
          minWidth: 0,
          flex: 1,
        }}
      >
        <Typography
          variant="h6"
          title={user.username}
          sx={(theme) => ({
            width: '100%',
            color: theme.palette.primary.main,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'left',
            textShadow: `0 0 12px ${alpha(theme.palette.primary.main, 0.55)}`,
          })}
        >
          {user.username}
        </Typography>
        <Chip
          label={subtitle}
          size="small"
          variant="outlined"
          sx={(theme) => ({
            color: participant.status === 'invited'
              ? theme.palette.secondary.main
              : theme.palette.green.main,
            borderColor: participant.status === 'invited'
              ? theme.palette.secondary.main
              : theme.palette.green.main,
            bgcolor: alpha(theme.palette.background.default, 0.78),
          })}
        />
      </Stack>

      {participant.permissions?.can_remove && (
        <Button
          variant="outlined"
          aria-label={`Remove ${user.username}`}
          title={`Remove ${user.username}`}
          disabled={saving}
          onClick={() => onRemove(participant)}
          sx={(theme) => ({
            ...removeButtonSx(theme),
            position: 'absolute',
            zIndex: 2,
            right: 12,
            bottom: 12,
          })}
        >
          <UserMinus size={17} />
        </Button>
      )}
    </Box>
  );
}
